import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Calendar, DateObject} from "react-multi-date-picker";
import {InputMask} from "primereact/inputmask";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {toDateObject} from "../utils";
import {useTranslate} from "../useTranslate";
import TimeDropdownButton from "./time-dropdown-button";
import Icon from "./icon";
import "./zofino-datepicker.css";

const EMPTY_RANGE = {start: null, end: null};
const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function timestamp(date) {
    if (!date) return null;
    if (typeof date.toUnix === "function") {
        const unix = date.toUnix();
        return Number.isFinite(unix) ? unix : null;
    }
    if (date instanceof Date) return Math.floor(date.getTime() / 1000);

    const value = new Date(date).getTime();
    return Number.isNaN(value) ? null : Math.floor(value / 1000);
}

function normalizeDateValue(date) {
    if (!date || date.isValid === false) return null;
    if (typeof date.toUnix === "function") return timestamp(date) === null ? null : date;

    try {
        const normalized = new DateObject(date);
        return normalized.isValid ? normalized : null;
    } catch {
        return null;
    }
}

function sameDate(left, right) {
    return timestamp(left) === timestamp(right);
}

function sameRange(left = EMPTY_RANGE, right = EMPTY_RANGE) {
    return sameDate(left.start, right.start) && sameDate(left.end, right.end);
}

function normalizeRange(value = EMPTY_RANGE) {
    return {
        start: normalizeDateValue(value?.start),
        end: normalizeDateValue(value?.end),
    };
}

function cloneRecurrence(value) {
    if (!value) return null;
    return {
        ...value,
        weekDays: Array.isArray(value.weekDays) ? [...value.weekDays] : [],
    };
}

function recurrenceSignature(value) {
    return value ? JSON.stringify(cloneRecurrence(value)) : "";
}

function preferredActiveField(isRange, value = EMPTY_RANGE) {
    if (!isRange) return "end";
    return value?.start ? "end" : "start";
}

function resolveActiveField(isRange, value, requestedField) {
    if (requestedField === "end") return "end";
    if (requestedField === "start" && isRange) return "start";
    return preferredActiveField(isRange, value);
}

function dayTimestamp(date) {
    const nativeDate = date?.toDate?.() ?? (date instanceof Date ? new Date(date) : null);
    if (!nativeDate || Number.isNaN(nativeDate.getTime())) return null;

    nativeDate.setHours(0, 0, 0, 0);
    return nativeDate.getTime();
}

export function getDayVisualState({
    currentDay,
    startDay = null,
    endDay = null,
    previewDay = null,
    activeField = "start",
    minDay = null,
    maxDay = null,
    externallyDisabled = false,
}) {
    const hasRange = startDay !== null && endDay !== null;
    const rangeStart = hasRange ? Math.min(startDay, endDay) : null;
    const rangeEnd = hasRange ? Math.max(startDay, endDay) : null;
    const isDisabled = Boolean(
        externallyDisabled ||
        (minDay !== null && currentDay < minDay) ||
        (maxDay !== null && currentDay > maxDay) ||
        (activeField === "start" && endDay !== null && currentDay > endDay) ||
        (activeField === "end" && startDay !== null && currentDay < startDay)
    );
    const canPreview = activeField === "end" &&
        startDay !== null &&
        endDay === null &&
        previewDay !== null &&
        previewDay >= startDay;
    const previewStart = canPreview ? startDay : null;
    const previewEnd = canPreview ? previewDay : null;

    return {
        isRangeStart: currentDay === startDay,
        isRangeEnd: currentDay === endDay,
        isInRange: rangeStart !== null && currentDay > rangeStart && currentDay < rangeEnd,
        isVisualStart: rangeStart !== null && currentDay === rangeStart,
        isVisualEnd: rangeEnd !== null && currentDay === rangeEnd,
        isSingleSelected: !hasRange && (currentDay === startDay || currentDay === endDay),
        isPreviewStart: previewStart !== null && currentDay === previewStart,
        isPreviewEnd: previewEnd !== null && currentDay === previewEnd,
        isPreviewRange: previewStart !== null && currentDay > previewStart && currentDay < previewEnd,
        isDisabled,
        canStartPreview: activeField === "end" &&
            startDay !== null &&
            endDay === null &&
            currentDay >= startDay &&
            !isDisabled,
    };
}

function formatDate(date) {
    return date?.format?.("YYYY/MM/DD") || "";
}

function applyTimeToDate(dateTimeString, timeHHMM) {
    if (typeof dateTimeString !== "string" || typeof timeHHMM !== "string") return "";

    const dateMatch = dateTimeString.match(/^(\d{4}\/\d{2}\/\d{2})/);
    return dateMatch ? `${dateMatch[1]} ${timeHHMM}` : "";
}

function isCompleteDate(value) {
    return /^\d{4}\/\d{2}\/\d{2}$/.test(value);
}

function parseDateInput(value) {
    if (!isCompleteDate(value)) return null;
    const date = toDateObject(value);
    return date?.isValid && date.format("YYYY/MM/DD") === value ? date : null;
}

function ZofinoDatepicker({
                              value = EMPTY_RANGE,
                              onChange,
                              config = {},
                              recurrence = null,
                              onRecurrenceChange,
                              renderRecurrence,
                              sessionKey = 0,
                              initialActiveField,
                              onApply,
                              onCancel,
                              // the scheduling assistant beside the picker reads the draft and can set it ({start, end, nonce});
                              // the draft is applied only with «اعمال», as before (docs/tasks/35-schedule-assistant-progress-chart.md)
                              onDraftChange,
                              externalDraft,
                          }) {
    const translate = useTranslate();
    const isRange = Boolean(config.range);
    const configuredMapDays = config.mapDays;
    const minDayTimestamp = dayTimestamp(config.minDate);
    const maxDayTimestamp = dayTimestamp(config.maxDate);
    const initialRange = useMemo(() => normalizeRange(value), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [activeField, setActiveField] = useState(() => resolveActiveField(isRange, initialRange, initialActiveField));
    const [hoveredDay, setHoveredDay] = useState(null);
    const [draftRange, setDraftRange] = useState(initialRange);
    const [draftRecurrence, setDraftRecurrence] = useState(() => cloneRecurrence(recurrence));
    const draftRangeRef = useRef(initialRange);
    const committedRangeRef = useRef(initialRange);
    const draftRecurrenceRef = useRef(cloneRecurrence(recurrence));
    const committedRecurrenceRef = useRef(cloneRecurrence(recurrence));
    const onChangeRef = useRef(onChange);
    const onRecurrenceChangeRef = useRef(onRecurrenceChange);
    const externalValueRef = useRef(value);
    const externalRecurrenceRef = useRef(recurrence);
    externalValueRef.current = value;
    externalRecurrenceRef.current = recurrence;

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        onRecurrenceChangeRef.current = onRecurrenceChange;
    }, [onRecurrenceChange]);

    const valueStartTimestamp = timestamp(value?.start);
    const valueEndTimestamp = timestamp(value?.end);
    const externalRecurrenceSignature = recurrenceSignature(recurrence);
    const startDayTimestamp = dayTimestamp(draftRange.start);
    const endDayTimestamp = dayTimestamp(draftRange.end);

    // A new external value or picker session starts with a clean draft. Calendar
    // navigation and draft editing never echo data back to the parent.
    useEffect(() => {
        const nextRange = normalizeRange(externalValueRef.current);
        const nextRecurrence = cloneRecurrence(externalRecurrenceRef.current);
        committedRangeRef.current = nextRange;
        draftRangeRef.current = nextRange;
        committedRecurrenceRef.current = nextRecurrence;
        draftRecurrenceRef.current = nextRecurrence;
        setDraftRange(nextRange);
        setDraftRecurrence(nextRecurrence);
        setHoveredDay(null);
        setActiveField(resolveActiveField(isRange, nextRange, initialActiveField));
    }, [externalRecurrenceSignature, initialActiveField, isRange, sessionKey, valueStartTimestamp, valueEndTimestamp]);

    const onDraftChangeRef = useRef(onDraftChange);
    onDraftChangeRef.current = onDraftChange;
    useEffect(() => {
        onDraftChangeRef.current?.({start: timestamp(draftRange.start), end: timestamp(draftRange.end)});
    }, [draftRange]);

    useEffect(() => {
        if (!externalDraft?.nonce) return;
        const nextRange = normalizeRange({start: externalDraft.start, end: externalDraft.end});
        draftRangeRef.current = nextRange;
        setDraftRange(nextRange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalDraft?.nonce]);

    const updateField = useCallback((field, nextDate) => {
        const previous = draftRangeRef.current;
        if (sameDate(previous[field], nextDate)) return false;

        const nextTimestamp = timestamp(nextDate);
        if (nextDate && nextTimestamp === null) return false;

        if (
            nextDate &&
            ((field === "start" && previous.end && nextTimestamp > timestamp(previous.end)) ||
                (field === "end" && previous.start && nextTimestamp < timestamp(previous.start)))
        ) {
            return false;
        }

        const nextRange = {...previous, [field]: nextDate ?? null};
        draftRangeRef.current = nextRange;
        setDraftRange(nextRange);
        return true;
    }, []);

    const handleCalendarChange = useCallback((selectedDate) => {
        const updated = updateField(activeField, selectedDate || null);
        if (updated && isRange && activeField === "start") {
            setActiveField("end");
        }
        return updated;
    }, [activeField, isRange, updateField]);

    const handleActiveFieldChange = useCallback((field) => {
        setHoveredDay(null);
        setActiveField(field);
    }, []);

    const handleStartChange = useCallback((date) => updateField("start", date), [updateField]);
    const handleEndChange = useCallback((date) => updateField("end", date), [updateField]);
    const handleQuickDateChange = useCallback((action) => {
        if (action === "none") {
            const cleared = {start: null, end: null};
            draftRangeRef.current = cleared;
            setDraftRange(cleared);
            setActiveField(isRange ? "start" : "end");
            return;
        }
        if (action === "custom") return;

        const offset = action === "tomorrow" ? 1 : action === "next-week" ? 7 : 0;
        const nextDate = new DateObject()
            .setCalendar(persian)
            .setLocale(persian_fa)
            .add(offset, "day");
        if (updateField(activeField, nextDate) && isRange && activeField === "start") {
            setActiveField("end");
        }
    }, [activeField, isRange, updateField]);

    const handleDraftRecurrenceChange = useCallback((nextRecurrence) => {
        const normalized = cloneRecurrence(nextRecurrence);
        draftRecurrenceRef.current = normalized;
        setDraftRecurrence(normalized);
    }, []);

    const resetDraft = useCallback(() => {
        const nextRange = normalizeRange(externalValueRef.current);
        const nextRecurrence = cloneRecurrence(externalRecurrenceRef.current);
        committedRangeRef.current = nextRange;
        draftRangeRef.current = nextRange;
        committedRecurrenceRef.current = nextRecurrence;
        draftRecurrenceRef.current = nextRecurrence;
        setDraftRange(nextRange);
        setDraftRecurrence(nextRecurrence);
        setHoveredDay(null);
        setActiveField(resolveActiveField(isRange, nextRange, initialActiveField));
    }, [initialActiveField, isRange]);

    const recurrenceFrequency = draftRecurrence?.frequency === "custom"
        ? draftRecurrence?.customFrequency
        : draftRecurrence?.frequency;
    const recurrenceValid = !draftRecurrence ||
        recurrenceFrequency !== "weekly" ||
        (draftRecurrence.weekDays || []).length > 0;
    const rangeValid = !draftRange.start || !draftRange.end ||
        timestamp(draftRange.start) <= timestamp(draftRange.end);

    const handleApply = useCallback(() => {
        if (!rangeValid || !recurrenceValid) return;
        const nextRange = draftRangeRef.current;
        const nextRecurrence = cloneRecurrence(draftRecurrenceRef.current);

        if (!sameRange(committedRangeRef.current, nextRange)) {
            onChangeRef.current?.(nextRange);
        }
        if (recurrenceSignature(committedRecurrenceRef.current) !== recurrenceSignature(nextRecurrence)) {
            onRecurrenceChangeRef.current?.(nextRecurrence);
        }

        committedRangeRef.current = nextRange;
        committedRecurrenceRef.current = nextRecurrence;
        onApply?.({value: nextRange, recurrence: nextRecurrence});
    }, [onApply, rangeValid, recurrenceValid]);

    const handleCancel = useCallback(() => {
        resetDraft();
        onCancel?.();
    }, [onCancel, resetDraft]);

    const decorateDay = useCallback((dayProps) => {
        const existingDecoration = typeof configuredMapDays === "function"
            ? configuredMapDays(dayProps)
            : null;
        const decoration = existingDecoration?.constructor === Object
            ? existingDecoration
            : {};
        const currentDay = dayTimestamp(dayProps.date);
        if (currentDay === null) return decoration;

        const visualState = getDayVisualState({
            currentDay,
            startDay: startDayTimestamp,
            endDay: endDayTimestamp,
            previewDay: hoveredDay,
            activeField,
            minDay: minDayTimestamp,
            maxDay: maxDayTimestamp,
            externallyDisabled: decoration.disabled,
        });
        const hasDecoration = visualState.isRangeStart ||
            visualState.isRangeEnd ||
            visualState.isInRange ||
            visualState.isSingleSelected ||
            visualState.isPreviewStart ||
            visualState.isPreviewEnd ||
            visualState.isPreviewRange;
        const shouldManageHover = activeField === "end" &&
            startDayTimestamp !== null &&
            endDayTimestamp === null;

        if (!hasDecoration && !visualState.isDisabled && !shouldManageHover) return decoration;

        return {
            ...decoration,
            disabled: visualState.isDisabled,
            className: [
                decoration.className,
                visualState.isRangeStart && "zofino-range-start",
                visualState.isRangeEnd && "zofino-range-end",
                visualState.isInRange && "zofino-range-middle",
                visualState.isVisualStart && "zofino-visual-start",
                visualState.isVisualEnd && "zofino-visual-end",
                visualState.isSingleSelected && "zofino-single-selected",
                visualState.isPreviewStart && "zofino-preview-start",
                visualState.isPreviewEnd && "zofino-preview-end",
                visualState.isPreviewRange && "zofino-preview-middle",
            ].filter(Boolean).join(" "),
            onMouseEnter: (event) => {
                decoration.onMouseEnter?.(event);
                if (visualState.canStartPreview) setHoveredDay(currentDay);
            },
            onMouseLeave: (event) => {
                decoration.onMouseLeave?.(event);
                setHoveredDay((previous) => previous === currentDay ? null : previous);
            },
        };
    }, [
        activeField,
        configuredMapDays,
        endDayTimestamp,
        hoveredDay,
        maxDayTimestamp,
        minDayTimestamp,
        startDayTimestamp,
    ]);

    const recurrenceContent = renderRecurrence?.({
        value: draftRecurrence,
        onChange: handleDraftRecurrenceChange,
        startWeekDay: draftRange.start?.toDate?.().getDay?.() ?? null,
        startDate: draftRange.start,
    });

    return (
        <div className="z-datepicker" dir="rtl">
            <InputDate
                range={isRange}
                value={draftRange}
                activeField={activeField}
                onActiveFieldChange={handleActiveFieldChange}
                onStartChange={handleStartChange}
                onEndChange={handleEndChange}
            />
            <div className="zofino-datepicker-main">
                <QuickDate onChange={handleQuickDateChange}/>
                <div className="zofino-calendar-surface">
                    <Calendar
                        {...config}
                        range={false}
                        value={draftRange[activeField] ?? null}
                        onChange={handleCalendarChange}
                        mapDays={decorateDay}
                        digits={DIGITS}
                        format="YYYY/MM/DD HH:mm"
                        className="shadow-none"
                    />
                </div>
            </div>
            {recurrenceContent}
            <div className="zofino-datepicker-actions">
                <button type="button" className="zofino-datepicker-cancel" onClick={handleCancel}>
                    {translate("cancel")}
                </button>
                <button
                    type="button"
                    className="zofino-datepicker-apply"
                    disabled={!rangeValid || !recurrenceValid}
                    onClick={handleApply}
                >
                    {translate("apply")}
                </button>
            </div>
        </div>
    );
}

const InputDate = memo(function InputDate({
    value = EMPTY_RANGE,
    range,
    activeField,
    onActiveFieldChange,
    onStartChange,
    onEndChange,
}) {
    const translate = useTranslate();
    const [startDateString, setStartDateString] = useState(() => formatDate(value.start));
    const [endDateString, setEndDateString] = useState(() => formatDate(value.end));

    useEffect(() => {
        setStartDateString(formatDate(value.start));
    }, [value.start]);

    useEffect(() => {
        setEndDateString(formatDate(value.end));
    }, [value.end]);

    const handleStartInputChange = useCallback((event) => {
        const nextValue = event.target.value || "";
        setStartDateString(nextValue);

        if (!nextValue.replace(/\D/g, "")) onStartChange(null);
        else if (isCompleteDate(nextValue)) {
            const nextDate = parseDateInput(nextValue);
            if (!nextDate || !onStartChange(nextDate)) {
                setStartDateString(formatDate(value.start));
            }
        }
    }, [onStartChange, value.start]);

    const handleEndInputChange = useCallback((event) => {
        const nextValue = event.target.value || "";
        setEndDateString(nextValue);

        if (!nextValue.replace(/\D/g, "")) onEndChange(null);
        else if (isCompleteDate(nextValue)) {
            const nextDate = parseDateInput(nextValue);
            if (!nextDate || !onEndChange(nextDate)) {
                setEndDateString(formatDate(value.end));
            }
        }
    }, [onEndChange, value.end]);

    const handleStartFocus = useCallback(() => onActiveFieldChange("start"), [onActiveFieldChange]);
    const handleEndFocus = useCallback(() => onActiveFieldChange("end"), [onActiveFieldChange]);

    const handleStartTimeSelect = useCallback((time) => {
        const nextValue = applyTimeToDate(startDateString, time);
        if (!nextValue) return;
        if (onStartChange(toDateObject(nextValue))) setStartDateString(nextValue);
    }, [onStartChange, startDateString]);

    const handleEndTimeSelect = useCallback((time) => {
        const nextValue = applyTimeToDate(endDateString, time);
        if (!nextValue) return;
        if (onEndChange(toDateObject(nextValue))) setEndDateString(nextValue);
    }, [endDateString, onEndChange]);

    return (
        <div className="zofino-date-fields">
            {range && (
                <>
                    <DateField
                        active={activeField === "start"}
                        label={translate("date-picker.start-label")}
                        value={value.start}
                        inputValue={startDateString}
                        placeholder={translate("date-picker.start-placeholder")}
                        autoFocus={activeField === "start"}
                        onFocus={handleStartFocus}
                        onChange={handleStartInputChange}
                        onTimeSelect={handleStartTimeSelect}
                    />
                    <Icon icon="mi mi:arrow_left_alt" className="text-sm zofino-date-range-arrow"/>
                </>
            )}
            <DateField
                active={activeField === "end"}
                label={translate(range ? "date-picker.end-label" : "date-picker.date-label")}
                value={value.end}
                inputValue={endDateString}
                placeholder={translate("date-picker.end-placeholder")}
                autoFocus={!range || activeField === "end"}
                onFocus={handleEndFocus}
                onChange={handleEndInputChange}
                onTimeSelect={handleEndTimeSelect}
            />
        </div>
    );
}, (previous, next) => (
    previous.range === next.range &&
    previous.activeField === next.activeField &&
    sameRange(previous.value, next.value) &&
    previous.onActiveFieldChange === next.onActiveFieldChange &&
    previous.onStartChange === next.onStartChange &&
    previous.onEndChange === next.onEndChange
));

function DateField({active, label, value, inputValue, placeholder, autoFocus, onFocus, onChange, onTimeSelect}) {
    const translate = useTranslate();
    return (
        <div
            data-testid={active ? `active-date-field-${label}` : undefined}
            className={`zofino-date-field ${active ? "zofino-date-field-active" : ""}`}
            onClick={onFocus}
        >
            <label className="zofino-date-field-label">{label}</label>
            <div className="zofino-date-field-control">
                <InputMask
                    mask="9999/99/99"
                    autoClear={false}
                    autoFocus={autoFocus}
                    aria-label={label}
                    value={inputValue}
                    placeholder={!value ? placeholder : undefined}
                    onChange={onChange}
                    onInput={onChange}
                    onFocus={onFocus}
                    className="border-none shadow-none flex-1 text-xs bg-transparent text-right"
                />
                {value ? (
                    <TimeDropdownButton
                        placement="left"
                        label={translate("add-time")}
                        value={value.format("HH:mm")}
                        stepMinutes={15}
                        onSelect={onTimeSelect}
                    />
                ) : null}
            </div>
        </div>
    );
}

const QuickDate = memo(function QuickDate({onChange}) {
    const translate = useTranslate();
    const items = useMemo(() => [
        {title: "today", action: "today", icon: "today"},
        {title: "tomorrow", action: "tomorrow", icon: "event_upcoming"},
        {title: "next-week", action: "next-week", icon: "date_range"},
        {title: "date-picker.no-date", action: "none", icon: "event_busy"},
        {title: "date-picker.custom-date", action: "custom", icon: "calendar_month"},
    ], []);

    return (
        <div className="zofino-quick-actions" role="group" aria-label={translate("date-picker.quick-actions")}>
            {items.map((item) => (
                <button key={item.action} type="button" onClick={() => onChange(item.action)}>
                    <Icon icon={`mi mi:${item.icon}`} className="text-sm"/>
                    <span>{translate(item.title)}</span>
                </button>
            ))}
        </div>
    );
}, (previous, next) => previous.onChange === next.onChange);

export default memo(ZofinoDatepicker);
